import {
  deleteAddress,
  deleteDraftAddress,
  getUserAddressList,
  updateAddress,
} from '@/apis/address/address.api';
import { Address } from '@/apis/address/address.interface';
import AddressAdd from '@/assets/icons/address/AddressAdd.svg';
import AddressDelete from '@/assets/icons/address/AddressDelete.svg';
import AddressEdit from '@/assets/icons/address/AddressEdit.svg';
import VectorDown from '@/assets/icons/VectorDownBig.svg';
import NewOrEditAddress from '@/components/address/AddressCreate';
import { getAddressStore } from '@/components/address/AddressStore';
import SiteChangeConfirmDialog from '@/components/countrySelect/SiteChangeConfirmDialog';
import UKCopy from '@/components/UKCopy';
import UKDialog from '@/components/UKDialog';
import UKPageError from '@/components/UKPageError';
import UKPageLoading from '@/components/UKPageLoading';
import UKPageNothing, { DefaultImageType } from '@/components/UKPageNothing';
import UKToast from '@/components/UKToast/index';
import UKTruncatedTooltip from '@/components/UKTruncatedTooltip/index';
import { PAGE_TYPE } from '@/constants/address';
import { useTranslation } from '@/i18n/client';
import { useStores } from '@/models';
import { EXPOSURE_RECORD, sendClickTrack, sendPv } from '@/tracks/25422';
import {
  EXPOSURE_RECORD as DELETE_EXPOSURE_RECORD,
  sendClickTrack as sendClickTrackDelete,
} from '@/tracks/25424';
import { curPostCodeOrAddress, setDefaultLocalAddress } from '@/utils/addressRequestUtils';
import { clearAddressCookie, initAddress, setAddressCookie } from '@/utils/addressUtils';
import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  IconButton,
  Radio,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import to from 'await-to-js';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import {
  ADDRESS_CATEGORY_VALUES,
  ADDRESS_EVENT_NAMES,
  addressBusinessLogger,
} from '@/utils/logger/instance/addressBusinessLogger';
import styles from './index.module.scss';

import { itemClickTrack } from '@/utils/addressTrackUtils';

export interface DeliveryListRef {
  openCreateAddressModal: () => void; // 创建地址
}

interface DataItemProps {
  addressList?: Address[] | null;
  isModal?: boolean;
  isPersonModal?: boolean; //个人中心地址入口
  getAddress?: () => void;
}

const DeliveryList = forwardRef<DeliveryListRef, DataItemProps>(
  ({ isPersonModal, getAddress }, ref) => {
    const { t } = useTranslation();
    const addressStore = getAddressStore();
    const { countryStore } = useStores();
    const [addressList, setAddressList] = useState<Address[] | null>(null);
    const [unSyncAddressList, setUnSyncAddressList] = useState<Address[] | null>(null); // 联合登录英国站地址
    const [isLoading, setIsLoading] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const [isError, setIsError] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [openCreateModal, setOpenCreateModal] = useState(false);
    const [curAddress, setCurAddress] = useState<Address | null>(null);
    const [againDelete, setAgainDelete] = useState(false); // 删除二次确认弹窗
    const [curSelectIndex, setCurSelectIndex] = useState(-1); // 当前选中的角标

    const [showSiteChangeDialog, setShowSiteChangeDialog] = useState(false); // 控制跨站点确认弹窗显示
    const [otherSiteAddressInfo, setOtherSiteAddressInfo] = useState<Address | null>(null);

    useImperativeHandle(ref, () => ({
      openCreateAddressModal: () => {
        setOpenCreateModal(true);
      },
    }));

    useEffect(() => {
      getAddressList(true);
      if (isPersonModal) {
        sendPv.NeworderReceiverList();
      }
    }, []);

    useEffect(() => {
      // 如果是个人中心地址 只有一条数据需要默认选中且置灰
      if (isPersonModal && addressList && addressList.length === 1 && !addressList[0]?.defAddress) {
        requestUpdateAddress({
          item: addressList[0],
          isSelect: true,
        });
        return;
      }
      if (addressList && addressList.length > 0) {
        handleRadioChange(addressList[0], 0, 'init');
      }
    }, [addressList]);

    // 判断是否为Address类型的类型守卫函数
    const isAddress = (data: Address | null): data is Address => {
      if (!data) {
        return false;
      }
      return (data as Address).addressId !== undefined;
    };

    // 判断是否为PostCodeAddress类型的类型守卫函数
    const isPostCodeAddress = (data: Address | null): data is Address => {
      if (!data) {
        return false;
      }
      return (data as Address).postCode !== undefined && !(data as Address).addressId;
    };

    // 匹配地址的函数
    const matchAddress = (identifier: string | number, addressList: Address[] | null) => {
      if (!addressList) {
        return;
      }

      let matchedIndex: number = -1;

      if (typeof identifier === 'string') {
        matchedIndex = addressList.findIndex((item) => item.postCode === identifier);
      } else if (typeof identifier === 'number') {
        matchedIndex = addressList.findIndex((item) => item.addressId === identifier);
      }
      if (matchedIndex !== -1) {
        // 如果找到匹配的元素，选中该元素
        if (matchedIndex !== 0) {
          const matchedItem = addressList[matchedIndex];
          addressList.splice(matchedIndex, 1);
          addressList.unshift(matchedItem);
        }
      }
      setAddressList(addressList);
    };

    // 更新列表数据（包含缓存处理）
    const updateAddressList = async (list: Address[]) => {
      if (!list || list.length === 0) {
        setAddressList(null);
        return;
      }
      const address = await initAddress();
      // 无缓存 直接返回
      if (!address) {
        setAddressList(list);
        return;
      }
      // 有缓存则获取缓存匹配的到值排序到第一条
      if (isAddress(address)) {
        matchAddress(address.addressId, list);
        return;
      }
      if (isPostCodeAddress(address)) {
        // @ts-ignore
        matchAddress(address.postCode, list);
        return;
      }
      // 未匹配成功兜底直接返回
      setAddressList(list);
    };

    const getAddressList = async (isInit: boolean = false) => {
      if (isInit) {
        setIsLoading(true);
      }
      const params = {
        source: isPersonModal ? 1 : 3,
        displayOverArea: isPersonModal,
      };
      const [err, result] = await to(getUserAddressList(params));
      if (isInit) {
        setIsLoading(false);
      }
      setIsLoaded(true);
      if (err) {
        setAddressList(null);
        if (isPersonModal) {
          setIsError(true);
          return;
        }
      } else {
        if (result?.code === '401') {
          if (isPersonModal) {
            setIsError(false);
            return;
          }
          return;
        }
        const normalList = result?.data?.addressList || [];
        const undeliverableAddressList =
          result?.data?.undeliverableAddressList?.map((item) => {
            return {
              ...item,
              isDeliverable: false,
              displayOverArea: true,
            };
          }) || [];
        const unSupportedSiteAddressList =
          result?.data?.unSupportedSiteAddressList?.map((item) => {
            return {
              ...item,
              otherSite: true, // 跨站点地址
            };
          }) || [];
        const list = [...normalList, ...undeliverableAddressList, ...unSupportedSiteAddressList];
        if (list) {
          if (isPersonModal) {
            setIsError(false);
            setAddressList(list as Address[]);
          } else {
            updateAddressList(list);
          }
        } else {
          if (isPersonModal) {
            setIsError(true);
            return;
          }
        }
        const unList = result?.data?.unSyncAddressList;
        setUnSyncAddressList(unList);
        // 删除、编辑需要更新缓存数据
        const postCode = await curPostCodeOrAddress();
        const detailPostCode = await curPostCodeOrAddress(true);
        addressStore.setPostCode(postCode);
        addressStore.setDetailPostCode(detailPostCode);
      }

      if (result?.code !== '200') {
        // 地址列表查询异常上报
        addressBusinessLogger({
          eventName: ADDRESS_EVENT_NAMES.ADDRESS_LIST_QUERY_ERROR,
          category: {
            module: ADDRESS_CATEGORY_VALUES.MODULE.ADDRESS_MANAGE,
            action: ADDRESS_CATEGORY_VALUES.ACTION.REQUEST,
            result: ADDRESS_CATEGORY_VALUES.RESULT.ERROR,
          },
          message: {
            response: JSON.stringify(result),
            error: JSON.stringify(err),
          },
        });
      }
    };

    const handleExpand = () => {
      setIsExpanded(!isExpanded);
    };

    const handleClose = () => {
      setOpenCreateModal(false);
      setCurAddress(null);
      if (getAddress) {
        getAddress();
      }
    };

    const handleAgainClose = () => {
      setAgainDelete(false);
      setCurAddress(null);
    };

    const clearLocal = async () => {
      const localData = await initAddress();
      if (curAddress && localData) {
        const data = localData as Address;
        if (curAddress?.addressId === data?.addressId) {
          clearAddressCookie();
          // 删除过后，需要再次缓存默认、或者第一条
          setDefaultLocalAddress();
        }
      }
    };

    const handleAgainConfirm = async () => {
      if (!curAddress) {
        return;
      }
      const [err, result] = await to(
        curAddress?.unSync === 1
          ? deleteDraftAddress({ addressId: curAddress?.addressId })
          : deleteAddress({ addressId: curAddress?.addressId }),
      );
      if (result?.code === '200') {
        handleAgainClose();
        // 如果删除的数据跟缓存的数据是同一条，需要删除缓存数据
        clearLocal();
        if (getAddressList) {
          getAddressList();
        }
        UKToast.show({
          content: `${t('address.deleteSuccessful')}`,
          icon: 'success',
        });
      } else {
        if (result?.msg) {
          UKToast.show({
            content: result?.msg,
            icon: 'error',
          });
        }
      }
    };

    const requestUpdateAddress = async ({
      item,
      isSelect,
      showToast,
    }: {
      item: Address;
      isSelect: boolean;
      showToast?: boolean;
    }) => {
      if (!item) {
        return;
      }
      item.defAddress = isSelect;
      const [err, result] = await to(
        updateAddress({
          ...item,
        }),
      );
      if (result?.code === '200') {
        if (showToast) {
          const content: any = isSelect
            ? `${t('address.setDefaultAddressTips')}`
            : `${t('address.setUnDefaultAddressTips')}`;
          UKToast.show({
            content,
            icon: 'success',
          });
        }
        if (getAddressList) {
          getAddressList();
        }
      } else {
        if (result?.msg) {
          UKToast.show({
            content: result?.msg,
            icon: 'error',
          });
        }
      }
    };

    // type init｜changeAddress 分别代表初始化和 切换改变
    const handleRadioChange = async (item: Address, index: number, type: string) => {
      setCurSelectIndex(index);
      // 结算切换需要写缓存
      if (type === 'changeAddress') {
        setAddressCookie(item);
        const postCode = await curPostCodeOrAddress();
        const detailPostCode = await curPostCodeOrAddress(true);
        addressStore.setPostCode(postCode);
        addressStore.setDetailPostCode(detailPostCode);
        countryStore.updateCurrentAddress(item);
        if (getAddress) {
          getAddress();
        }
      }
    };

    const handleCardItemClick = (item: Address, index: number) => {
      if (item.otherSite) {
        // 选择跨站点地址 弹窗提示是否要切换站点
        setOtherSiteAddressInfo(item);
        setShowSiteChangeDialog(true);
        return;
      }
      if (!isPersonModal && item?.unSync !== 1) {
        handleRadioChange(item, index, 'changeAddress');
        sendClickTrack.NeworderReceiverList_SelectAddress(itemClickTrack(index, item));
      }
    };

    // 计算地址总数量（普通地址 + 草稿地址）
    const getTotalAddressCount = () => {
      const addressCount = addressList ? addressList.length : 0;
      const unSyncAddressCount = unSyncAddressList ? unSyncAddressList.length : 0;
      return addressCount + unSyncAddressCount;
    };

    if (isLoading) {
      return <UKPageLoading />;
    }

    const cardFloor = (item: Address, index: number) => {
      return (
        <div className={styles.box} style={{ width: isPersonModal ? '100%' : '90%' }}>
          <div className={styles.flex}>
            <UKTruncatedTooltip
              text={item?.name}
              tooltipStyles={styles.country}
              tooltipProps={{
                placement: 'top',
                arrow: true,
              }}
            />
            <div className={styles.phone}>
              {item?.areaCode ? '+' + item?.areaCode : ''} {item?.mobile}
            </div>
          </div>
          <div className={styles.flexRight}>
            <UKTruncatedTooltip
              text={item?.fullAddress}
              textLines={2}
              tooltipStyles={styles.address}
              tooltipProps={{
                placement: 'top',
                arrow: true,
              }}
            />
            {isPersonModal && item?.unSync !== 1 && (
              <UKCopy
                text={`${item?.name} +${item?.areaCode} ${item?.mobile}\n${item?.fullAddress}`}
              >
                <span
                  className={styles.copy}
                  exptag={'exp|' + EXPOSURE_RECORD.NeworderReceiverList_Copy_Expo}
                  data-exptag-config='{"useClick": false, "stay_duration": 0.3, "repeated":false, "area_rate": 0.01}'
                  data-exptag-json-param={JSON.stringify({
                    ...itemClickTrack(index, item),
                    from_page: isPersonModal ? '1' : '2',
                  })}
                  onClick={(event) => {
                    event.stopPropagation();
                    sendClickTrack.NeworderReceiverList_Copy(itemClickTrack(index, item));
                  }}
                >
                  {t('address.copy')}
                </span>
              </UKCopy>
            )}
          </div>
          <div className={styles.flexTop9}>
            <div
              className={styles.flex}
              exptag={'exp|' + EXPOSURE_RECORD.NeworderReceiverList_Default_Expo}
              data-exptag-config='{"useClick": false, "stay_duration": 0.3, "repeated":false, "area_rate": 0.01}'
              data-exptag-json-param={JSON.stringify({
                ...itemClickTrack(index, item),
                from_page: isPersonModal ? '1' : '2',
              })}
            >
              {/* 草稿(联合登录地址) item?.unSync === 1  isDeliverable 代表是否可配送 */}
              {item?.unSync === 1 ||
              (item?.isDeliverable === false && item?.displayOverArea) ||
              item?.otherSite ? (
                <span className={styles.draftItemText}>
                  {item?.unSync === 1
                    ? `${t('address.updateAddress')}`
                    : item?.otherSite
                      ? `${t('address.otherRegionsAddresses')}`
                      : `${t('address.delivery')}`}
                </span>
              ) : (
                <>
                  <Checkbox
                    sx={{
                      color: '#E0E1E5',
                      padding: '0px',
                      marginLeft: '-2px',
                      '&.Mui-checked': {
                        color:
                          item?.defAddress &&
                          isPersonModal &&
                          addressList &&
                          addressList.length === 1
                            ? '#E0E1E5'
                            : '#1A1A1A',
                      },
                    }}
                    // 如果是个人中心地址 只有一条数据需要默认选中且置灰
                    disabled={
                      isPersonModal && addressList && addressList.length === 1 ? true : false
                    }
                    checked={item?.defAddress}
                    onClick={(event) => {
                      if (!isPersonModal) {
                        event.stopPropagation();
                      }
                      const target = event.target as HTMLInputElement;
                      requestUpdateAddress({
                        item,
                        isSelect: target.checked,
                        showToast: true,
                      });
                      sendClickTrack.NeworderReceiverList_Default({
                        index,
                        addrid: `${item?.addressId}`,
                        chstatus: target.checked ? '1-选中' : '0-取消',
                      });
                    }}
                  />
                  <div className={styles.setText}>
                    {item?.defAddress
                      ? `${t('address.defaultAddress')}`
                      : `${t('address.setDefaultAddress')}`}
                  </div>
                </>
              )}
            </div>
            <div className={styles.flex}>
              <div
                className={styles.addressActions}
                exptag={'exp|' + EXPOSURE_RECORD.NeworderReceiverList_Edit_Expo}
                data-exptag-config='{"useClick": false, "stay_duration": 0.3, "repeated":false, "area_rate": 0.01}'
                data-exptag-json-param={JSON.stringify({
                  ...itemClickTrack(index, item),
                  from_page: isPersonModal ? '1' : '2',
                })}
              >
                <AddressEdit
                  onClick={(event: any) => {
                    // if (!isPersonModal) {
                    event.stopPropagation();
                    // }
                    setOpenCreateModal(true);
                    setCurAddress(item);
                    sendClickTrack.NeworderReceiverList_Edit(itemClickTrack(index, item));
                  }}
                />
              </div>
              {(isPersonModal || item?.unSync === 1) && (
                <div
                  className={styles.addressActions}
                  style={{
                    display: addressList?.length === 1 && !isPersonModal ? 'none' : 'block',
                  }}
                  exptag={'exp|' + EXPOSURE_RECORD.NeworderReceiverList_Delete_Expo}
                  data-exptag-config='{"useClick": false, "stay_duration": 0.3, "repeated":false, "area_rate": 0.01}'
                  data-exptag-json-param={JSON.stringify({ from_page: isPersonModal ? '1' : '2' })}
                >
                  <AddressDelete
                    onClick={(event: any) => {
                      // if (!isPersonModal) {
                      event.stopPropagation();
                      // }
                      setAgainDelete(true);
                      setCurAddress(item);
                      sendClickTrack.NeworderReceiverList_Delete({
                        from_page: isPersonModal ? '1' : '2',
                      });
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      );
    };

    const getMaxDraftDisplayCount = (addressListLength: number): number => {
      if (addressListLength === 0) return 10;

      const maxCount = 10 - Math.ceil(addressListLength / 2) * 2;

      return Math.max(maxCount, 0);
    };

    const draftList = () => {
      const addressListLength = addressList?.length || 0;

      // 计算在折叠状态下应该显示的草稿地址数量
      const displayCount = !isExpanded
        ? getMaxDraftDisplayCount(addressListLength)
        : unSyncAddressList?.length || 0;

      if (displayCount === 0 || !unSyncAddressList || unSyncAddressList.length === 0) {
        return null;
      }

      return (
        <>
          <span
            className={styles.draftTitle}
            style={{ paddingLeft: isPersonModal ? '16px' : '0px' }}
          >
            {t('address.draftAddresses')}
          </span>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            {unSyncAddressList &&
              unSyncAddressList.length > 0 &&
              (displayCount < unSyncAddressList.length
                ? unSyncAddressList.slice(0, displayCount)
                : unSyncAddressList
              ).map((item, index) => [
                <Grid
                  size={unSyncAddressList && unSyncAddressList?.length === 1 ? 12 : 6}
                  key={index}
                >
                  <div className={styles.flexLeftGray}>
                    {/* 地址卡片 */}
                    {cardFloor(item, index)}
                  </div>
                </Grid>,
              ])}
          </Grid>
        </>
      );
    };

    return (
      <div
        id='address-manage-delivery-list'
        className={styles.container}
        exptag={'exp|' + EXPOSURE_RECORD.NeworderReceiverList_AddressQuantityAuto}
        data-exptag-config='{"useClick": false, "stay_duration": 0.3, "repeated":false, "area_rate": 0.01}'
        data-exptag-json-param={JSON.stringify({
          addqty: addressList?.length,
          from_page: isPersonModal ? '1' : '2',
        })}
      >
        {/* 添加地址 */}
        {/* {isPersonModal && isLoaded && (
        <div
          className={styles.flexHeaderPerson}
          exptag={"exp|" + EXPOSURE_RECORD.NeworderReceiverList_Add_Expo}
          data-exptag-config='{"useClick": false, "stay_duration": 0.3, "repeated":false, "area_rate": 0.01}'
          data-exptag-json-param={JSON.stringify({ from_page: isPersonModal ? "1" : "2" })}
        >
          <div className={styles.titlePerson}>{t("address.addresses")}</div>
          <Button
            className={styles.add}
            onClick={() => {
              setOpenCreateModal(true);
              sendClickTrack.NeworderReceiverList_Add({ from_page: isPersonModal ? "1" : "2" });
            }}
            style={{ opacity: addressList && addressList?.length >= 30 ? 0.4 : 1 }}
            disabled={addressList && addressList?.length >= 30 ? true : false}
          >
            <AddressAdd /> {t("address.addAddress")}
          </Button>
        </div>
      )} */}
        {isError && (
          <div className={styles.errorPage}>
            <UKPageError
              style={{ marginTop: '60px' }}
              clickTryAgain={() => getAddressList()}
              text={t('common.pageStatus.error')}
              btnText={t('common.btnText.tryAgain')}
            />
          </div>
        )}
        {!isPersonModal && isLoaded && (
          <>
            <div className={styles.title}>{t('address.shippingAddress')}</div>
            <div className={styles.info}>{t('address.lib.info')}</div>
          </>
        )}
        {/* 地址列表 */}
        <div className={!isPersonModal ? styles.noScrollbars : ''}>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            {addressList &&
              addressList.length > 0 &&
              (!isExpanded && isPersonModal ? addressList.slice(0, 10) : addressList).map(
                (item, index) => [
                  <Grid size={addressList && addressList?.length === 1 ? 12 : 6} key={index}>
                    <div
                      // item.otherSite 其他站点地址 不做选中效果
                      className={
                        curSelectIndex === index && !item.otherSite
                          ? styles.flexLeft
                          : styles.flexLeftGray
                      }
                      exptag={'exp|' + EXPOSURE_RECORD.NeworderReceiverList_Address_Card_Expo}
                      data-exptag-config='{"useClick": false, "stay_duration": 0.3, "repeated":false, "area_rate": 0.01}'
                      data-exptag-json-param={JSON.stringify({
                        ...itemClickTrack(index, item),
                        from_page: isPersonModal ? '1' : '2',
                      })}
                      onClick={() => {
                        handleCardItemClick(item, index);
                      }}
                    >
                      {!isPersonModal && (
                        <Radio
                          checked={curSelectIndex === index}
                          value='a'
                          name='radio-buttons'
                          style={{ padding: 0 }}
                          inputProps={{ 'aria-label': 'A' }}
                          disableRipple
                        />
                      )}
                      {/* 地址卡片 */}
                      {cardFloor(item, index)}
                    </div>
                  </Grid>,
                ],
              )}
          </Grid>
          {/* 展开更多地址 */}
          {getTotalAddressCount() === 0 && (
            <div className={styles.errorPage}>
              <UKPageNothing
                defaultImageType={DefaultImageType.ADDRESS}
                style={{ marginTop: '60px' }}
                text={t('common.nothingMore')}
              />
            </div>
          )}
          {isPersonModal && unSyncAddressList && unSyncAddressList.length > 0 && draftList()}
        </div>
        {/* 个人中心 */}
        {getTotalAddressCount() > 10 && isPersonModal && !isExpanded && (
          <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <div
              className={`${styles.expanded} ${styles.personExpand}`}
              onClick={() => handleExpand()}
            >
              {t('address.viewMore')}
              <VectorDown width={10} className={styles.svgExpand} style={{ top: '0px' }} />
            </div>
          </div>
        )}
        {!isPersonModal && isLoaded && (
          <div className={styles.bottomFloat}>
            <Button
              className={styles.add}
              onClick={() => setOpenCreateModal(true)}
              style={{ opacity: addressList && addressList?.length >= 30 ? 0.4 : 1 }}
              disabled={addressList && addressList?.length >= 30 ? true : false}
            >
              <AddressAdd /> {t('address.addAddress')}
            </Button>
          </div>
        )}
        {/* 创建地址 弹窗 */}
        <UKDialog
          open={openCreateModal}
          onClose={handleClose}
          showCloseBtn={true}
          sx={{
            '& .MuiDialog-paper': {
              width: '880px', // 设置自定义宽度
              Height: '596px',
              // padding: "0px",
            },
          }}
        >
          <IconButton
            aria-label='close'
            onClick={() => {
              setOpenCreateModal(false);
              setCurAddress(null);
            }}
            sx={(theme) => ({
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
            })}
          >
            <CloseIcon />
          </IconButton>
          <Typography sx={{ p: 2, padding: 0 }}>
            <NewOrEditAddress
              data={curAddress}
              handleClose={handleClose}
              isModal={true}
              getAddress={getAddress}
              getAddressList={getAddressList}
              pageType={isPersonModal ? PAGE_TYPE.PERSON : PAGE_TYPE.SHIPPING}
            />
          </Typography>
        </UKDialog>
        {/* 删除二次确认弹窗 */}
        <Dialog
          open={againDelete}
          onClose={handleAgainClose}
          aria-labelledby='alert-dialog-title'
          aria-describedby='alert-dialog-description'
        >
          <DialogContent>
            <DialogContentText id='alert-dialog-description'>
              {t('address.deleteText')}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              variant='outlined'
              exptag={'exp|' + DELETE_EXPOSURE_RECORD.NeworderAddressDelete_Cancel_Expo}
              data-exptag-config='{"useClick": false, "stay_duration": 0.3, "repeated":false, "area_rate": 0.01}'
              data-exptag-json-param={{}}
              onClick={() => {
                handleAgainClose();
                sendClickTrackDelete.NeworderAddressDelete_Cancel();
              }}
            >
              {t('address.cancel')}
            </Button>
            <Button
              variant='contained'
              exptag={'exp|' + DELETE_EXPOSURE_RECORD.NeworderAddressDelete_Expo}
              data-exptag-config='{"useClick": false, "stay_duration": 0.3, "repeated":false, "area_rate": 0.01}'
              data-exptag-json-param={{}}
              onClick={() => {
                handleAgainConfirm();
                sendClickTrackDelete.NeworderAddressDelete();
              }}
            >
              {t('address.confirm')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* 跨站点确认弹窗 */}
        <SiteChangeConfirmDialog
          open={showSiteChangeDialog}
          toCountryCode={otherSiteAddressInfo?.countryCode || ''}
          toCountryName={
            otherSiteAddressInfo?.countryNameMultiLan || otherSiteAddressInfo?.countryName || ''
          }
          setDialogOpen={setShowSiteChangeDialog}
        />
      </div>
    );
  },
);

DeliveryList.displayName = 'DeliveryList';

export default DeliveryList;
